import {
  Button,
  Heading,
  Input,
  LoadingSpinner,
  makeCurrencyOptions,
  SerialNumberHelpModal,
  isSerialNumberValid,
  filterAllowedCountries
} from '@rabbit/elements/shared-components';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  DTVendable,
  FBD_Holding_Private,
  PersonaTypeSingleLetter,
  PersonaIdTypeSplitter,
  UserUploadedDocument,
  TenantInfo,
  FBD_HoldingProxy,
} from '@rabbit/data/types';
import {
  CompletedUploadShape,
  DocTypeShapeTypes,
  OliveHoldingRegistrationFormData,
  UploadedFileCategories,
  DEFAULT_ACCEPTED_FILE_TYPES
} from '@rabbit/elements/shared-types';
import { toast } from 'react-toastify';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  useEffect,
} from 'react';
import {
  FileStorageContext,
  getCompletedUploadsOfCategory,
  LIST_COUNTRIES,
  useCreateHoldingFromVendable,
  useSendEmail,
} from '@rabbit/bizproc/react';
import { UserContext } from '@rabbit/olive/context/UserContext';
import { OurDateTime } from '@rabbit/utils/ts';
import { t } from 'i18next';
import { AppContext, createDynamicYupSchema } from '@rabbit/app-context';
import { OliveFileUploader } from '@rabbit/olive/components/organisms/upload-wrapper/OliveFileUploader';
import { useMyConsumerPersona } from '@rabbit/data/portal';
import { AdditionalFields, RequiredType, DisplayType } from '@rabbit/helper';
import { getRetailerById } from '@rabbit/sage/utils/helpers.tsx';
import CommonFormBuilder from 'libs/elements/shared-components/src/lib/molecules/CommonFormBuilder/CommonFormBuilder';
import {
  nestApiGetMakesModels,
  nestApiCheckHoldingUniqueCodes,
} from '@rabbit/bizproc/core';

//@ts-ignore
const isDemoEnv = import.meta.env.VITE_DEMO;

// Filter fields based on display values and DisplayType.DISPLAY_FOR_CONSUMER
const filterFieldsByDisplaySettingForConsumer = (
  fields: AdditionalFields
): AdditionalFields => {
  return Object.entries(fields)
    .filter(([_, field]) => {
      // If no display property or empty display array, allow the field
      if (!field.display || field.display.length === 0) {
        return true;
      }

      // If display includes DISPLAY_FOR_CONSUMER, allow the field
      return field.display.includes(DisplayType.DISPLAY_FOR_CONSUMER);
    })
    .reduce((acc, [key, field]) => {
      acc[key] = field;
      return acc;
    }, {} as AdditionalFields);
};

interface FormValuesShape {
  purchase_time: Date | null;
  purchase_price: {
    amount: number;
    currency: string;
  };
  purchase_location: {
    docid: string;
    country: string;
  } | null;
  store_not_listed: boolean;
  custom_store_name: string;
  purchase_country: string;
  serial_number: string;
  serial_proof: UserUploadedDocument[];
  [key: string]: any;
}

interface RegisterHoldingInformationStepProps {
  onChange: (value: number, holdingCreationResponse: string) => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setProductRegistrationData: React.Dispatch<OliveHoldingRegistrationFormData>;
  product: DTVendable;
}

export function RegisterHoldingInformationStep({
  onChange,
  setIsLoading,
  product,
}: RegisterHoldingInformationStepProps) {
  const tenantLink = t('tenantLink');
  const CFG_CLAIMS_FLOW = t('CFG_CLAIMS_FLOW');
  const { config, tenantInfo } = useContext(AppContext);
  // Check vendable-specific setting first, then fall back to global config
  const serialNumberRequired =
    product?.serialNumberRequired ?? config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_REQUIRED;
  const serialNumberValidationEnabled =
    product?.serialNumberEnabled ?? config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED;
  const persona = useMyConsumerPersona();
  const createUserHolding = useCreateHoldingFromVendable(product.docid);

  const [fields, setFields] = useState<any>(
    config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS
  );

  const allowedCurrencies = makeCurrencyOptions(tenantInfo as TenantInfo);

  // Build allowed countries list (default to LIST_COUNTRIES)
  const allowedCountries = filterAllowedCountries(
    (tenantInfo as any)?.allowedCountries as string[] | undefined
  );

  const validationSchema = Yup.object().shape({
    purchase_location: Yup.mixed()
      .nullable()
      .when('store_not_listed', {
        is: false,
        then: (schema) => schema.required('Please select a purchase location'),
        otherwise: (schema) => schema.nullable(),
      }),
    store_not_listed: Yup.boolean(),
    custom_store_name: Yup.string().when('store_not_listed', {
      is: true,
      then: (schema) => schema.trim().required('Please enter the store name'),
      otherwise: (schema) => schema.notRequired(),
    }),
    purchase_country: Yup.string().when('store_not_listed', {
      is: true,
      then: (schema) =>
        schema.trim().required('Please select the purchase country'),
      otherwise: (schema) => schema.notRequired(),
    }),
    ...(config?.HOLDINGS?.MANDATORY_FIELDS?.PURCHASE_PRICE
      ? {
        purchase_price: Yup.object({
          amount: Yup.number()
            .min(1, t('message.pleaseEnterValidAmount'))
            .required(t('message.pleaseEnterValidAmount')),
          currency: Yup.string()
            .trim()
            .required(t('message.currencyIsRequired')),
        })
          .required(t('message.pleaseEnterPurchasePrice'))
          .typeError(t('message.pleaseEnterPurchasePrice')),
      }
      : {
        purchase_price: Yup.object({
          amount: Yup.number(),
          currency: Yup.string(),
        }),
      }),
    purchase_time: Yup.date()
      .max(new Date(), 'Please enter a valid date')
      .required('Please select a purchase time')
      .typeError('Please enter a valid date'),
    ...(isDemoEnv ||
      (serialNumberRequired && {
        serial_number: Yup.string()
          .trim()
          .required('Please enter a serial number'),
      })),
    serial_proof: serialNumberRequired
      ? Yup.array().min(1).required('Please upload a serial number proof')
      : Yup.array(),
    ...createDynamicYupSchema(
      config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS
        ? filterFieldsByDisplaySettingForConsumer(
          config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS
        )
        : {},
      RequiredType.REQUIRED_FOR_CONSUMER
    ).fields,
  });

  const [isSubmiting, setSubmiting] = useState(false);
  const [isSerialHelpOpen, setIsSerialHelpOpen] = useState(false);
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);
  const [serialNumberRanges, setSerialNumberRanges] = useState<
    Array<{ start: string; end: string }>
  >([]);

  const { uploadQueueState, moveCompletedUploadsToAttached } =
    useContext(FileStorageContext) || {};
  const { completed } = uploadQueueState ?? {};
  const serialNumberProofUploads: CompletedUploadShape[] =
    getCompletedUploadsOfCategory(
      completed ?? [],
      UploadedFileCategories.SerialNumberProof
    );
  const serialNumber = window.localStorage.getItem('LRP_VendableSn') ?? '';
  //const formikRef = useRef<FormikProps<FormValuesShape> | null>(null);
  const additionalValues: { [key: string]: any } = {};

  if (config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS) {
    const additionalFields: AdditionalFields =
      config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS;
    const filteredFields =
      filterFieldsByDisplaySettingForConsumer(additionalFields);

    for (const key in filteredFields) {
      if (Object.prototype.hasOwnProperty.call(filteredFields, key)) {
        const element = filteredFields[key];
        additionalValues[key] = element.initialValue;
      }
    }
  }

  const initialValues: FormValuesShape = {
    purchase_time: null,
    purchase_price: {
      amount: 0,
      currency: tenantInfo?.currency || 'GBP',
    },
    purchase_location: null,
    serial_number: serialNumber,
    store_not_listed: false,
    custom_store_name: '',
    purchase_country: '',
    serial_proof: [],
    ...additionalValues,
  };

  // Store serial numbers and ranges for validation
  useEffect(() => {
    if (product && serialNumberValidationEnabled) {
      setSerialNumbers(product.serialNumbers || []);
      setSerialNumberRanges(product.serialNumberRanges || []);
    }
  }, [product, serialNumberValidationEnabled]);

  const holding_private = FBD_Holding_Private.empty();
  const holdingId = config?.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER_PROOF
    ? FBD_Holding_Private.keygen(holding_private, FBD_Holding_Private)
    : '';

  const onSubmit = async (values: FormValuesShape) => {
    const serialValidationEnabledNow =
      serialNumberValidationEnabled && product.serialNumberEnabled;

    // Check if serial number verification is required and if it's verified
    let serialVerified = false;
    if (
      config?.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER &&
      product.serialNumberEnabled &&
      config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED
    ){
      const serialNumber = values.serial_number;
      if (!serialNumber) {
        toast.error(t('general.pleaseEnterSerialNumber'));
        return;
      }
      const isValid = isSerialNumberValid(
        serialNumber,
        serialNumbers,
        serialNumberRanges
      );
      serialVerified = isValid;
    }

    setSubmiting(true);
    setIsLoading(true);

    // If values include a retailer from the database, then purchase_location_other should be cleared - and vice versa
    const newPurchaseLocation =
      values.custom_store_name && values.store_not_listed
        ? null
        : values.purchase_location?.docid ?? '';

    const newPurchaseLocationOther = values.store_not_listed
      ? values.custom_store_name ?? ''
      : null;

    let additionalData: Record<string, any> = {};

    if (config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS) {
      const additionalFields: AdditionalFields =
        config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS;
      const filteredFields =
        filterFieldsByDisplaySettingForConsumer(additionalFields);

      // Ensure additionalData exists in values
      additionalData = {};
      for (const key in filteredFields) {
        if (
          Object.prototype.hasOwnProperty.call(
            values as Record<string, any>,
            key
          )
        ) {
          additionalData[key] = values?.[key] ?? null; // Use null as fallback if missing
        }
      }
    }

    // Check for unique code validation via API
    try {
      const uniqueCodesToCheck: Record<string, string> = {};

      if (config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS) {
        const additionalFields: AdditionalFields =
          config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS;
        const filteredFields =
          filterFieldsByDisplaySettingForConsumer(additionalFields);

        for (const fieldKey in filteredFields) {
          const field = filteredFields[fieldKey];
          const fieldValue = values[fieldKey];
          if (
            field?.validation?.unique &&
            typeof fieldValue === 'string' &&
            fieldValue.trim()
          ) {
            uniqueCodesToCheck[fieldKey] = fieldValue.trim();
          }
        }
      }

      if (Object.keys(uniqueCodesToCheck).length > 0) {
        const mfr = `${PersonaTypeSingleLetter.Manufacturer}${PersonaIdTypeSplitter}${tenantLink}`;

        const resp = await nestApiCheckHoldingUniqueCodes({
          tenantLink,
          mfr,
          uniqueCodes: uniqueCodesToCheck,
        });

        const dups = resp?.data?.duplicates || {};
        const dupKeys = Object.keys(dups);
        if (dupKeys.length > 0) {
          dupKeys.forEach((key) => {
            const fieldConf =
              config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS?.[key];
            const label = fieldConf?.label || key;
            toast.error(
              t('message.uniqueCodeAlreadyExists', {
                codeType: label,
                code: dups[key].code,
              })
            );
          });
          setSubmiting(false);
          setIsLoading(false);
          return;
        }
      }
    } catch (error) {
      console.error('Error checking unique codes via API:', error);
      toast.error(t('general.somethingWentWrongPleaseTryAgain'));
      setSubmiting(false);
      setIsLoading(false);
      return;
    }

    const data = {
      purchase_location: newPurchaseLocation,
      purchase_location_other: newPurchaseLocationOther,
      purchase_price: {
        amount: Number(values.purchase_price.amount),
        currency: values.purchase_price.currency,
      },
      purchase_time: values.purchase_time
        ? OurDateTime.dateToTimestamp(values.purchase_time)
        : 0,
      serial: values?.serial_number ?? '',
      purchase_country: values.purchase_location
        ? values.purchase_location.country
        : values.purchase_country,
      serial_proof: values.serial_proof || [],
      additionalData,
      ...(serialValidationEnabledNow && {
        serialNumberVerified: serialVerified,
      }),
    };

    try {
      const res = await createUserHolding(data);
      const serialNumberProofFiles = serialNumberProofUploads?.flatMap((file) =>
        file.category === UploadedFileCategories.SerialNumberProof &&
          file?.uploadedFile
          ? [file.uploadedFile]
          : []
      );
      if (res) {
        if (
          moveCompletedUploadsToAttached &&
          serialNumberProofFiles &&
          serialNumberProofFiles.length > 0
        ) {
          moveCompletedUploadsToAttached(serialNumberProofUploads);
        }
      }
      if (window.localStorage.getItem('LRP_VendableSn')) {
        window.localStorage.removeItem('LRP_VendableSn');
      }
      toast.success(
        'Product registered successfully! Please upload your receipt.'
      );
      setIsLoading(false);
      if (typeof res === 'string') onChange(2, res);
    } catch (err) {
      toast.error('Something went wrong, please try again.');
      setSubmiting(false);
      setIsLoading(false);
      console.log(err);
    }
  };

  const getAdditionalFields = async () => {
    try {
      const data: any = await nestApiGetMakesModels({
        tenantLink: tenantLink,
      });
      let tempFields = { ...fields };
      if (data.makes) {
        tempFields['make'].suggestion =
          data.makes?.map((s: any) => s.label) || [];
      }
      if (data.models) {
        tempFields['model'].suggestion =
          data.models?.map((s: any) => s.label) || [];
      }
      setFields(tempFields);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (config.MAKE_TAB.ENABLED && config.MODEL_TAB.ENABLED) {
      getAdditionalFields();
    }
  }, [config.MAKE_TAB.ENABLED, config.MODEL_TAB.ENABLED]);

  // interrupted-onboarding may not have identity/persona ready immediately.
  // Avoid crashing the page; let portal services + persona creation finish first.
  if (!persona?.personaId || !persona?.data?.consumer_private) {
    return <LoadingSpinner size="sm" />;
  }

  if (!CFG_CLAIMS_FLOW) return <LoadingSpinner size="sm" />;


  return (
    <div className="flex flex-col gap-4">
      <Heading kind={'h3'}>When and where did you buy your product?</Heading>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={false}
        validateOnBlur={false}
        validateOnChange={true}
        onSubmit={onSubmit}
      //innerRef={formikRef}
      >
        {({ errors, values }) => (
          <Form className="font-nunito flex flex-col gap-4">
            <Input
              type="datepicker"
              name="purchase_time"
              settings={{
                placeholder: 'Purchase date',
                maxDate: new Date(),
                hint: '*required',
              }}
            />
            <Input
              type="currency"
              name="purchase_price"
              label={
                tenantLink === 'SUPAGARD'
                  ? 'Supagard purchase price'
                  : undefined
              }
              settings={{
                id: 'purchase_price',
                placeholder: 'Purchase amount',
                currency:
                  values?.purchase_price?.currency ??
                  tenantInfo?.currency ??
                  'GBP',
                disableCurrency: false,
                inputTypeCurrencyOverrideOptions: allowedCurrencies,
                hint: config?.HOLDINGS?.MANDATORY_FIELDS?.PURCHASE_PRICE
                  ? '*required'
                  : undefined,
              }}
            />
            <Input
              type="autocomplete-location-retailer"
              name="purchase_location"
              settings={{
                placeholder: `Enter ${config.HOLDINGS.NEW_REGISTRATION_FLOW.DEFAULT_STORE_NAME} name or location or town`,
                isMulti: false,
                disabled: values.store_not_listed,
                tenantLink: tenantLink,
                options: [],
                hint: '*required',
                ...(config.HOLDINGS.NEW_REGISTRATION_FLOW
                  .STORES_FROM_FIRESTORE_RETAILERS && {
                  tenantLink: t('tenantLink'),
                }),
              }}
            />
            <Input
              type="checkbox"
              name="store_not_listed"
              settings={{
                checkboxLabel: `
                    <span class="capitalize">
                      ${config.HOLDINGS.NEW_REGISTRATION_FLOW.DEFAULT_STORE_NAME}
                    </span>
                    is not listed
                  `,
                checkboxLabelStyles: 'text-base text-gray-500',
              }}
            />
            {values.store_not_listed && (
              <>
                <Input
                  type="text"
                  name="custom_store_name"
                  settings={{
                    id: 'custom_store_name',
                    placeholder: `Enter ${config.HOLDINGS.NEW_REGISTRATION_FLOW.DEFAULT_STORE_NAME} name`,
                  }}
                />
                <Input
                  type="select"
                  label=""
                  name="purchase_country"
                  settings={{
                    options: allowedCountries,
                    id: 'purchase_country',
                    placeholder: 'The country where the product was purchased',
                  }}
                />
              </>
            )}
            {!config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_HIDE && (
              <div className="relative">
                <Input
                  type="text"
                  name="serial_number"
                  settings={{
                    placeholder: `Serial number ${serialNumberRequired ? '*' : ''
                      }`,
                    hint: serialNumberRequired ? '*required' : undefined,
                    disabled: serialNumber !== '',
                  }}
                />
              </div>
            )}
            {!config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_HIDE &&
              config?.HOLDINGS?.REGISTRATION?.SERIAL_NUMBER_HELP?.ENABLED && (
                <div className="-mt-2 text-sm text-gray-500">
                  <button
                    type="button"
                    className="cursor-pointer text-blue-600 underline"
                    onClick={() => setIsSerialHelpOpen(true)}
                  >
                    {config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_HELP.TEXT}
                  </button>

                  <SerialNumberHelpModal
                    isOpen={isSerialHelpOpen}
                    onClose={() => setIsSerialHelpOpen(false)}
                    vendable={product}
                  />
                </div>
              )}
            {serialNumberRequired && (
              <OliveFileUploader
                label={t('Photo of serial number') + '*'}
                identifiers={{
                  category: UploadedFileCategories.SerialNumberProof,
                  docType: {
                    docid: holdingId,
                    type: DocTypeShapeTypes.Holding,
                  },
                  personaId: persona?.personaId ?? '',
                }}
                name="serial_proof"
                shouldAutoUpdateDocs={false}
                currentFiles={values.serial_proof ?? []}
                maxFiles={2}
                accepts={DEFAULT_ACCEPTED_FILE_TYPES}
              />
            )}
            {Object.keys(additionalValues).length > 0 && (
              <CommonFormBuilder
                fields={
                  config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS
                    ? filterFieldsByDisplaySettingForConsumer(
                      config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS
                    )
                    : {}
                }
                userType="olive"
              />
            )}
            <Button
              kind="primary"
              type="submit"
              disabled={
                Object.keys(values).length === 0 ||
                Object.keys(errors).length > 0 ||
                (serialNumberRequired &&
                  values.serial_proof?.length === 0) ||
                // values?.purchase_time === 0 ||
                // values?.purchase_location?.length === 0 ||
                isSubmiting
              }
              loading={isSubmiting}
            >
              Confirm details
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default RegisterHoldingInformationStep;
